import { useEffect, useMemo, useState } from "react";
import { Checkbox, DiscountBadge } from "@components/atoms";
import { usePaymentMethods, type PaymentMethodCms } from "@shared/hooks";
import {
  getEventPaymentDiscounts,
  type EventPaymentDiscount,
} from "@services/discount.service";
import { env } from "@services/config/env";
import type { Event } from "@/types";

/** Maps a CMS payment method code to its flag field on the Event type. */
const PAYMENT_METHOD_FLAG: Record<string, keyof Pick<Event,
  "payment_method_payphone" | "payment_method_deuna" | "payment_method_jepfast" | "payment_method_cooperco" | "payment_method_plux"
>> = {
  PAYPHONE: "payment_method_payphone",
  DEUNA: "payment_method_deuna",
  JEP: "payment_method_jepfast",
  COOPERCO: "payment_method_cooperco",
  PLUX: "payment_method_plux",
};

/** Accepts the truthy representations that can come from PostgreSQL/legacy APIs. */
function isMarkedEnabled(value: unknown): boolean {
  return value === true || value === 1 || value === "1" || value === "t" || value === "true";
}

/**
 * CHILL&CHELA FESTIVALS (id 1860): mostrar un aviso de descuento por pagos con
 * tarjetas del Banco de Guayaquil arriba de los métodos de pago. Fijo solo para
 * este evento (pedido puntual). Si a futuro se necesita configurable por evento,
 * ver el pendiente de "nota de pago por evento" en el panel.
 */
const BANCO_GUAYAQUIL_DISCOUNT_EVENT_ID = 1860;

/**
 * Override de métodos de pago por evento: LISTA + ORDEN fijos (el orden normal viene del CMS
 * y es global; Plux queda al final). CHILL&CHELA / Oktoberfest (1860): solo Plux, DeUna y JEP,
 * en ese orden, SIN PayPhone (pedido del organizador). Es autoritativo para ese evento (no
 * depende de los flags per-evento). El resto de eventos usa los flags normales.
 */
const METHOD_ORDER_BY_EVENT: Record<number, string[]> = {
  [BANCO_GUAYAQUIL_DISCOUNT_EVENT_ID]: ["PLUX", "DEUNA", "JEP"],
};

interface PaymentStepProps {
  event: Event;
  selectedMethod: string;
  onMethodChange: (method: string) => void;
  termsAccepted: boolean;
  onTermsChange: (v: boolean) => void;
}

/**
 * Checkout step 2 — payment method selection.
 * medio_pago image, radio options, terms checkbox.
 * Payment methods loaded from CMS API (TKT-001 TASK-11).
 * Methods disabled per event configuration (TKT-PP-01).
 */
export function PaymentStep({
  event,
  selectedMethod,
  onMethodChange,
  termsAccepted,
  onTermsChange,
}: PaymentStepProps) {
  const { data: paymentMethods, isLoading } = usePaymentMethods();

  const [discounts, setDiscounts] = useState<EventPaymentDiscount[]>([]);

  useEffect(() => {
    let cancelled = false;
    getEventPaymentDiscounts(event.id).then((result) => {
      if (!cancelled) setDiscounts(result);
    });
    return () => {
      cancelled = true;
    };
  }, [event.id]);

  /**
   * Filter out methods that are explicitly disabled for this event.
   * A method is enabled when its flag is true OR absent/null (DB default = true).
   *
   * Plux go-live guard (parity with landings/appeventos/appticketin): even if
   * payment_method_plux is enabled on an event, never render the option until
   * the comercio email is configured — otherwise the button would build with
   * PayboxRemail: "" and fail silently for buyers.
   */
  const enabledMethods = useMemo(() => {
    if (!paymentMethods) return [];
    // Override por evento (lista + orden fijos). Autoritativo para ese evento; respeta el
    // guard de env de Plux (sin comercio email, no se muestra Plux aunque esté en la lista).
    const orderOverride = METHOD_ORDER_BY_EVENT[Number(event.id)];
    if (orderOverride) {
      const byCode = new Map(paymentMethods.map((m) => [m.code, m]));
      return orderOverride
        .filter((code) => code !== "PLUX" || Boolean(env.pluxComercioEmail))
        .map((code) => byCode.get(code))
        .filter((m): m is PaymentMethodCms => m != null);
    }
    return paymentMethods.filter((method) => {
      const flagKey = PAYMENT_METHOD_FLAG[method.code];
      if (!flagKey) return false;
      if (!isMarkedEnabled(event[flagKey])) return false;
      if (method.code === "PLUX" && !env.pluxComercioEmail) return false;
      return true;
    });
  }, [paymentMethods, event]);

  return (
    <div className="flex flex-col gap-4">
      <span className="font-semibold text-black" style={{ fontSize: 20 }}>
        Seleccionar método de pago
      </span>

      {/* Aviso de descuento — solo para CHILL&CHELA FESTIVALS (id 1860).
          Tonos de la marca Banco de Guayaquil (magenta #dd0066). */}
      {Number(event.id) === BANCO_GUAYAQUIL_DISCOUNT_EVENT_ID && (
        <div
          className="flex items-center gap-2.5 rounded-lg px-4 py-3"
          style={{ border: "1px solid #f4a9cb", backgroundColor: "#fdeef5" }}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="h-5 w-5 flex-shrink-0"
            style={{ color: "#dd0066" }}
          >
            <rect x="2" y="5" width="20" height="14" rx="2" />
            <line x1="2" y1="10" x2="22" y2="10" />
          </svg>
          <span className="font-semibold" style={{ fontSize: 13, color: "#b3004f" }}>
            Descuento por pagos con tarjetas del Banco de Guayaquil
          </span>
          <img
            src="/images/banco-guayaquil.png"
            alt="Banco de Guayaquil"
            className="ml-auto h-8 w-auto flex-shrink-0 object-contain"
          />
        </div>
      )}

      {/* Radio options */}
      <div className="flex flex-col gap-3">
        {isLoading && (
          <p className="text-primary-110" style={{ fontSize: 14 }}>
            Cargando métodos de pago...
          </p>
        )}
        {enabledMethods.map((opt) => {
          const discount = discounts.find(
            (d) => d.paymentMethodCode === opt.code && d.percentage > 0,
          );
          return (
          <label
            key={opt.id}
            className="flex h-[55px] cursor-pointer items-center gap-3 rounded-lg border border-primary-110 bg-white px-4 w-full"
            onClick={() => onMethodChange(opt.code)}
          >
            {/* Radio circle */}
            <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 border-primary-110">
              {selectedMethod === opt.code && (
                <div className="h-3 w-3 rounded-full bg-primary-110" />
              )}
            </div>
            {/* Payment method logo */}
            {opt.logo && (
              <img
                src={opt.logo}
                alt={opt.name}
                className="h-7 w-auto object-contain flex-shrink-0"
              />
            )}
            <span
              className="font-semibold text-primary-110"
              style={{ fontSize: 14 }}
            >
              {opt.name}
            </span>
            {discount && <DiscountBadge percentage={discount.percentage} />}
          </label>
          );
        })}
      </div>

      {/* Terms checkbox */}
      <Checkbox
        checked={termsAccepted}
        onChange={onTermsChange}
        label={
          <span className="text-primary-110" style={{ fontSize: 12 }}>
            He leído y acepto la Política de Uso de Datos Personales y los
            Términos y Condiciones
          </span>
        }
      />
    </div>
  );
}